import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await api.get('/auth/status', {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          if (response.data.authenticated) {
            setUser(response.data.user);
            setToken(savedToken);
          } else {
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Listen for automatic logout events from the API interceptor
  useEffect(() => {
    const handleAutoLogout = (event) => {
      console.log('Auto-logout triggered:', event.detail);

      // Clear user state
      setUser(null);
      setToken(null);

      // Show notification to user
      if (event.detail?.reason === 'token_expired') {
        // You can use a toast notification library here instead of alert
        setTimeout(() => {
          alert('Your session has expired. Please log in again.');
        }, 100);
      }
    };

    window.addEventListener('auth:logout', handleAutoLogout);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('auth:logout', handleAutoLogout);
    };
  }, []);

  const login = async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);

    // Fetch user info
    const userResponse = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);

    // Check if there's a redirect path stored (from session expiration)
    const redirectPath = localStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      localStorage.removeItem('redirectAfterLogin');
      // Return the redirect path so the login component can navigate
      return { user: userResponse.data, redirectTo: redirectPath };
    }

    return { user: userResponse.data, redirectTo: null };
  };

  const register = async (email, password) => {
    const response = await api.post('/auth/register', {
      email,
      password
    });

    // Auto-login after registration
    await login(email, password);
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
